import { queryOptions } from '@tanstack/react-query'

import { successStaleTime } from '@/shared/api/utils/query'
import { time } from '@/shared/datetime/utils/time'

import { getSession, listSessions } from './api'

import type { AIModel } from './constants'

/**
 * Query keys for AIR® AI chat sessions. Tenant is always the first key arg so
 * `invalidateQueries({ queryKey: sessionKeys.all })` clears every tenant scope
 * on tenant switch. `detail` also keys on `model` so a session id fetched
 * through standard vs beta doesn't share a cache entry — the bodies will
 * eventually converge but during the cutover they diverge.
 */
export const sessionKeys = {
	all: ['ai', 'sessions'] as const,
	list: (tenantId: string) => [...sessionKeys.all, 'list', tenantId] as const,
	detail: (sessionId: string, tenantId: string, model: AIModel) =>
		[...sessionKeys.all, 'detail', sessionId, tenantId, model] as const,
	/**
	 * Prefix used to invalidate / iterate both models of the same session's
	 * detail cache (rename / delete mutations need to clear both — backend
	 * sees one session id regardless of which endpoint pair fetched it).
	 */
	detailAllModels: (sessionId: string, tenantId: string) =>
		[...sessionKeys.all, 'detail', sessionId, tenantId] as const,
}

export const AI_MODELS: ReadonlyArray<AIModel> = ['standard', 'beta']

/**
 * Query options factory for AIR® AI chat sessions.
 */
export const sessionQueries = {
	list: (tenantId: string) =>
		queryOptions({
			queryKey: sessionKeys.list(tenantId),
			queryFn: ({ signal }) => listSessions(tenantId, signal),
			gcTime: time(5, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
		}),

	detail: (sessionId: string, tenantId: string, model: AIModel) =>
		queryOptions({
			queryKey: sessionKeys.detail(sessionId, tenantId, model),
			queryFn: ({ signal }) => getSession(sessionId, tenantId, model, signal),
			gcTime: time(5, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			enabled: !!sessionId && !!tenantId,
		}),
}
